import { type BoardFragmentFragment } from '@apps/www/src/__generated__/graphql';
import usePersistentSetting, {
	PersistentSettingKeys,
} from '@apps/www/src/www/hooks/usePersistentSetting';
import type { DraggableAttributes } from '@dnd-kit/core';
import { closestCenter, DndContext } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SVBoardThumbnail from '@pkgs/shared-client/components/SVBoardThumbnail';
import cn from '@pkgs/shared-client/helpers/cn';
import { formatRelativeDateShortToday } from '@pkgs/shared-client/helpers/format';
import plural from '@pkgs/shared-client/helpers/plural';
import { useBoardReorder } from '@pkgs/shared-client/hooks/useBoardReorder';
import IconHiddenSVG from '@pkgs/shared-client/img/icon-hidden-inlined.svg';
import BoardOwnershipType from '@pkgs/shared/enums/BoardOwnershipType';
import type BoardsSortMethod from '@pkgs/shared/enums/BoardsSortMethod';
import canReorderBoard from '@pkgs/shared/helpers/canReorderBoard';
import React, { useMemo } from 'react';
import SVA from './SVA';
import SVAvatarsStack from './SVAvatarsStack';
import SVButton, { SVButtonSIZES, SVButtonUSES } from './SVButton';
import SVLink from './SVLink';

const SortableItemWrapper = ({
	id,
	children,
}: {
	id: string;
	children: React.ReactElement<{
		dragHandleListeners?: SyntheticListenerMap;
		dragHandleAttributes?: DraggableAttributes;
	}>;
}) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				'-xl:w-1/6 -lg:w-1/4 -md:w-1/3 -lg:px-2 -md:pb-6 -sm:px-2 -sm:pb-6 -smp:w-1/2 box-border flex w-[12.5%] flex-shrink-0 flex-col space-y-4 px-2 pb-8',
				isDragging && 'z-index-dragging bg-background rounded-xl pb-2 pt-2',
			)}
		>
			{React.cloneElement(children, {
				dragHandleListeners: listeners,
				dragHandleAttributes: attributes,
			})}
		</div>
	);
};

const DragHandle = ({
	listeners,
	attributes,
}: {
	listeners: SyntheticListenerMap;
	attributes: DraggableAttributes;
}) => {
	const handleMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (listeners?.onMouseDown) {
			listeners.onMouseDown(e);
		}
	};

	return (
		<div
			className="group-hover:[transition:500 bg-background/50 absolute left-2 z-10 mt-2 cursor-grab rounded-md px-2 py-1 opacity-0 backdrop-blur-sm active:cursor-grabbing group-hover:opacity-100"
			onMouseDown={handleMouseDown}
			{...attributes}
			{...listeners}
		>
			<span className="text-sm text-gray-200">Drag</span>
		</div>
	);
};

const _Item = ({
	id,
	name,
	url,
	itemsCount,
	collaborators,
	invites,
	acceptURL,
	rejectURL,
	onBoardClick,
	board,
	lastItemAddedAt,
	dragHandleListeners,
	dragHandleAttributes,
	role,
	canEdit,
	sortMethod,
}: {
	id: BoardFragmentFragment['_id'];
	name: BoardFragmentFragment['name'];
	url: BoardFragmentFragment['url'];
	itemsCount: BoardFragmentFragment['itemsCount'];
	collaborators: BoardFragmentFragment['collaborators'];
	invites: BoardFragmentFragment['invites'];
	acceptURL: BoardFragmentFragment['acceptURL'];
	rejectURL: BoardFragmentFragment['rejectURL'];
	onBoardClick?: (board: BoardFragmentFragment['_id'] | null) => void;
	board: BoardFragmentFragment;
	lastItemAddedAt: BoardFragmentFragment['lastItemAddedAt'];
	dragHandleListeners?: SyntheticListenerMap;
	dragHandleAttributes?: DraggableAttributes;
	role: BoardFragmentFragment['role'];
	canEdit?: boolean;
	sortMethod: ValueOf<typeof BoardsSortMethod>;
}) => {
	const canReorder = role ? canReorderBoard(board.ownershipType, role, sortMethod) : false;

	return (
		<div className="group relative">
			{canReorder && dragHandleListeners && dragHandleAttributes && (
				<DragHandle listeners={dragHandleListeners} attributes={dragHandleAttributes} />
			)}
			<SVA
				Component={!onBoardClick ? SVLink : 'button'}
				className={cn(
					'group relative flex w-full flex-col space-y-1.5 leading-tight',
					acceptURL && rejectURL && 'opacity-60',
				)}
				to={url}
				onClick={onBoardClick ? () => onBoardClick(id) : undefined}
			>
				<SVBoardThumbnail
					className="rounded-lg pb-[100%]"
					board={board}
					hideEditIcon={!canEdit}
				/>
				<div className="flex items-start space-x-2">
					<div className="mb-2 flex-grow space-y-1.5">
						<div className="flex justify-between space-x-2">
							<span className="line-clamp-2 text-xl text-gray-50">{name}</span>

							{board.isHidden && (
								<span className="mt-1 flex items-start justify-center px-2">
									<IconHiddenSVG className="h-4 w-4 text-gray-500" />
								</span>
							)}
						</div>
						<div className="duration-over flex items-center space-x-4 text-start text-sm text-gray-500 transition-colors">
							<span>{plural(itemsCount, 'save')}</span>
							<span>
								{lastItemAddedAt
									? formatRelativeDateShortToday(lastItemAddedAt)
									: ''}
							</span>
						</div>
					</div>
				</div>
				{collaborators.length + invites.length > 1 && (
					<SVAvatarsStack
						users={collaborators}
						invites={invites}
						visibleCount={3}
						showDropdown={false}
					/>
				)}
			</SVA>
			{acceptURL && rejectURL && (
				<div className="-sm:flex-center mt-4 flex max-w-full space-x-2">
					<SVButton
						Component={SVLink}
						to={acceptURL}
						size={SVButtonSIZES.SMALL}
						use={SVButtonUSES.PRIMARY}
						className="-md:text-sm -md:px-4 -md:py-3 text-[15px]"
					>
						Accept
					</SVButton>
					<SVButton
						Component={SVLink}
						to={rejectURL}
						size={SVButtonSIZES.SMALL}
						className="-md:text-sm -md:px-4 -md:py-3 text-[15px]"
					>
						Decline
					</SVButton>
				</div>
			)}
		</div>
	);
};

const SVBoardsList = ({
	boards,
	showTitles,
	onBoardClick,
	canEdit = true,
}: {
	boards: BoardFragmentFragment[];
	showTitles: boolean;
	onBoardClick?: (boardId: BoardFragmentFragment['_id'] | null) => void;
	canEdit?: boolean;
}) => {
	const [sortMethod] = usePersistentSetting(PersistentSettingKeys.BOARDS_SORT_METHOD);

	const userBoards = useMemo(() => {
		return boards.filter((board) => board.ownershipType === BoardOwnershipType.USER);
	}, [boards]);

	const teamBoards = useMemo(() => {
		return boards.filter((board) => board.ownershipType === BoardOwnershipType.TEAM);
	}, [boards]);

	const { sensors, handleDragEnd } = useBoardReorder(boards);

	return (
		<div className="flex-col space-y-6">
			{userBoards.length > 0 && (
				<div>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={(event) => handleDragEnd(event)}
					>
						<SortableContext
							items={userBoards.map((b) => b._id)}
							strategy={rectSortingStrategy}
						>
							<ul className="-mx-2 flex flex-wrap">
								{userBoards.map((board) => (
									<SortableItemWrapper key={board._id} id={board._id}>
										<_Item
											id={board._id}
											name={board.name}
											url={board.url}
											itemsCount={board.itemsCount}
											collaborators={board.collaborators}
											invites={board.invites}
											acceptURL={board.acceptURL}
											rejectURL={board.rejectURL}
											onBoardClick={onBoardClick}
											board={board}
											lastItemAddedAt={board.lastItemAddedAt}
											role={board.role}
											sortMethod={sortMethod}
											canEdit={canEdit}
										/>
									</SortableItemWrapper>
								))}
							</ul>
						</SortableContext>
					</DndContext>
				</div>
			)}

			{teamBoards.length > 0 && (
				<div>
					{showTitles && <h2 className="type-small-title mb-6">Team boards</h2>}
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={(event) => handleDragEnd(event)}
					>
						<SortableContext
							items={teamBoards.map((b) => b._id)}
							strategy={rectSortingStrategy}
						>
							<ul className="-mx-2 flex flex-wrap">
								{teamBoards.map((board) => (
									<SortableItemWrapper key={board._id} id={board._id}>
										<_Item
											id={board._id}
											name={board.name}
											url={board.url}
											itemsCount={board.itemsCount}
											collaborators={board.collaborators}
											invites={board.invites}
											acceptURL={board.acceptURL}
											rejectURL={board.rejectURL}
											onBoardClick={onBoardClick}
											board={board}
											lastItemAddedAt={board.lastItemAddedAt}
											role={board.role}
											sortMethod={sortMethod}
											canEdit={canEdit}
										/>
									</SortableItemWrapper>
								))}
							</ul>
						</SortableContext>
					</DndContext>
				</div>
			)}
		</div>
	);
};

export default SVBoardsList;
